// src/services/affiliate-api.ts
// ============================================================================
// ADIM 8: Satış Ortaklığı - Frontend API Servisi
// ============================================================================

import { apiService } from './api';
import { isPanelMasked, PRIVACY_MASK } from '../utils/format';

// ============================================================================
// TYPES
// ============================================================================

export interface ReferredUser {
  id: string;
  maskedEmail: string;
  /** Bu müşteriden bugüne kadar kazanılan komisyon (tavan uygulanmış) */
  earnedFromCustomer?: number;
  /** Bu müşteriden kalan hak — 0 ise bu müşteriden yeni kazanç gelmez */
  capRemaining?: number;
  capReached?: boolean;
  registeredAt: string;
  isActive: boolean;
}

export interface CommissionPeriod {
  year: number;
  month: number;
  periodName: string;
  commission: number;
  activeUsersCount: number;
  paymentWindowStart: string;
  paymentWindowEnd: string;
  paymentWindowText: string;
  status: 'InProgress' | 'AwaitingPayment' | 'PaymentPending' | 'Paid' | 'CarriedOver';
  statusDescription: string;
  paidAtUtc?: string;
  transactionReference?: string;
  // Devir — asgari ödeme tutarının altında kalıp sonraki döneme ertelenen dönemler
  carriedToYear?: number;
  carriedToMonth?: number;
  carriedToPeriodName?: string;
  carryIns?: AffiliateCarryIn[];
  carryInAmount?: number;
  /** Bu dönemde fiilen ödenecek tutar = kendi komisyonu + devirler */
  payableAmount?: number;
}

export interface AffiliateCarryIn {
  year: number;
  month: number;
  periodName: string;
  commission: number;
}

export interface AffiliateStats {
  referralCode: string | null;
  referralCodeCreatedAt: string | null;
  totalReferrals: number;
  activeReferrals: number;
  totalAllTimeEarnings: number;
  totalPaidEarnings: number;
  totalPendingEarnings: number;
  /** MÜŞTERİ BAŞINA kazanç tavanı (USD): her yönlendirilen müşteriden en fazla bu kadar.
   *  Toplam kazanca sınır yoktur. Müşteri kırılımı referredUsers içinde. */
  perCustomerCap?: number;
  /** Tavanı dolmuş müşteri sayısı */
  customersAtCap?: number;
  /** Sonraki döneme devredilen toplam — bekleyene DAHİLDİR (para kaybolmadı) */
  totalCarriedOverEarnings?: number;
  carriedOverPeriods?: number;
  /** Asgari ödeme tutarı — devrin sebebini kullanıcıya yazmak için */
  minPayoutAmount?: number;
  currentMonthEarnings: number;
  nextPaymentDate: string | null;
  nextPaymentAmount: number | null;
  referredUsers: ReferredUser[];
  periods: CommissionPeriod[];
}

export interface CreateCodeResponse {
  message: string;
  referralCode: string;
  createdAt: string;
}

// Admin Types
export interface AdminAffiliateStats {
  totalCodes: number;
  activeAffiliates: number;
  totalReferredUsers: number;
  totalPaidAllTime: number;
  totalPaidThisMonth: number;
  totalPendingAmount: number;
  totalPendingCount: number;
}

export interface PendingPayment {
  userId: string;
  userName: string;
  userEmail: string;
  referralCode: string;
  year: number;
  month: number;
  periodName: string;
  commission: number;
  referredUsersCount: number;
  paymentWindowStart: string;
  /** Ödeme penceresi açıldı mı — false ise dönem "sıradaki" olarak gösterilir */
  paymentWindowOpen?: boolean;
  paymentMethodType: string;
  paymentMethodMasked: string | null;
  // DEVİR — kullanıcı kazancındaki ile aynı kural (tek genel eşik)
  carryInAmount?: number;
  carryIns?: AffiliateCarryIn[];
  /** Bu dönemde fiilen ödenecek tutar = kendi komisyonu + devirler */
  payableAmount?: number;
  minPayoutAmount?: number;
  /** Asgari tutarın altında → "sonraki döneme devret" butonu gösterilir */
  isBelowMinimum?: boolean;
}

export interface ReferralCode {
  id: string;
  code: string;
  userName: string;
  userEmail: string;
  createdAt: string | null;
  referredCount: number;
  totalEarnings: number;
  isActive: boolean;
}

export interface PaymentHistoryUser {
  userId: string;
  userName: string;
  userEmail: string;
  referralCode: string;
  totalPaid: number;
  paymentCount: number;
  payments: PaymentHistoryItem[];
}

export interface PaymentHistoryItem {
  id: string;
  periodName: string;
  year: number;
  month: number;
  /** Bankadan çıkan toplam (ana dönem + devirle kapanan dönemler) */
  commission: number;
  /** Ana dönemin kendi komisyonu */
  ownCommission?: number;
  carryInAmount?: number;
  carriedPeriods?: Array<{
    year: number;
    month: number;
    periodName: string;
    commission: number;
  }>;
  /** Bu transferle kapanan dönem sayısı (1 = devir yok) */
  periodCount?: number;
  paidAt: string;
  transactionReference: string | null;
}

export interface ApprovePaymentRequest {
  userId: string;
  year: number;
  month: number;
  transactionReference?: string;
  paymentNote?: string;
}

// ============================================================================
// USER API
// ============================================================================

/**
 * Kullanıcının satış ortaklığı istatistiklerini getirir
 */
export async function getAffiliateStats(): Promise<AffiliateStats> {
  const response = await fetch(`${getBaseUrl()}/api/affiliate/stats`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('İstatistikler yüklenemedi');
  }

  return response.json();
}

/**
 * Referans kodu oluşturur
 */
export async function createReferralCode(): Promise<CreateCodeResponse> {
  const response = await fetch(`${getBaseUrl()}/api/affiliate/create-code`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Kod oluşturulamadı');
  }

  return response.json();
}

/**
 * Belirli bir dönemin detaylarını getirir
 */
export async function getPeriodDetail(year: number, month: number): Promise<CommissionPeriod> {
  const response = await fetch(`${getBaseUrl()}/api/affiliate/periods/${year}/${month}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Dönem detayı yüklenemedi');
  }

  return response.json();
}

// ============================================================================
// ADMIN API
// ============================================================================

/**
 * Admin - Genel istatistikler
 */
export async function getAdminAffiliateStats(): Promise<AdminAffiliateStats> {
  const response = await fetch(`${getBaseUrl()}/api/admin/affiliate/stats`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('İstatistikler yüklenemedi');
  }

  return response.json();
}

/**
 * Admin - Bekleyen ödemeleri listele
 */
export async function getAdminPendingPayments(): Promise<PendingPayment[]> {
  const response = await fetch(`${getBaseUrl()}/api/admin/affiliate/pending-payments`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Bekleyen ödemeler yüklenemedi');
  }

  return response.json();
}

/**
 * Admin - Ödeme onayla
 */
export async function approveAffiliatePayment(request: ApprovePaymentRequest): Promise<any> {
  const response = await fetch(`${getBaseUrl()}/api/admin/affiliate/approve-payment`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Ödeme onaylanamadı');
  }

  return response.json();
}

/**
 * Admin - Birden fazla ortaklık dönemini tek seferde onayla.
 * Tek tek onayla aynı mantık: devredilmiş dönemler de aynı ödemeyle kapanır.
 */
export async function bulkApproveAffiliatePayments(request: {
  periods: Array<{ userId: string; year: number; month: number }>;
  transactionReference?: string;
  paymentNote?: string;
}): Promise<any> {
  const response = await fetch(`${getBaseUrl()}/api/admin/affiliate/bulk-approve`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Toplu ödeme başarısız oldu');
  }

  return response.json();
}

/**
 * Admin - Ortaklık dönemini bir sonraki döneme devret
 * (asgari ödeme tutarının altında kalan dönemler için; para taşınmaz, ödeme ertelenir)
 */
export async function carryOverAffiliatePayment(
  userId: string,
  year: number,
  month: number
): Promise<{ message: string; carriedToPeriodName: string; carriedAmount: number }> {
  const response = await fetch(`${getBaseUrl()}/api/admin/affiliate/carry-over`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, year, month }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Devir işlemi başarısız oldu');
  }

  return response.json();
}

/** Admin - Yanlış yapılan ortaklık devrini geri al */
export async function undoCarryOverAffiliatePayment(
  userId: string,
  year: number,
  month: number
): Promise<{ message: string }> {
  const response = await fetch(`${getBaseUrl()}/api/admin/affiliate/carry-over/undo`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, year, month }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Devir geri alınamadı');
  }

  return response.json();
}

/**
 * Admin - Tüm referans kodlarını listele
 */
export async function getAdminReferralCodes(): Promise<ReferralCode[]> {
  const response = await fetch(`${getBaseUrl()}/api/admin/affiliate/codes`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Kodlar yüklenemedi');
  }

  return response.json();
}

/**
 * Admin - Ödeme geçmişi (kullanıcı bazlı gruplandırılmış)
 */
export async function getAdminPaymentHistory(): Promise<PaymentHistoryUser[]> {
  const response = await fetch(`${getBaseUrl()}/api/admin/affiliate/payment-history`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Ödeme geçmişi yüklenemedi');
  }

  return response.json();
}

/**
 * Admin - Referans kodunu güncelle
 */
export async function updateReferralCode(userId: string, newCode: string): Promise<any> {
  const response = await fetch(`${getBaseUrl()}/api/admin/affiliate/codes/${userId}`, {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ newCode }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Kod güncellenemedi');
  }

  return response.json();
}

/**
 * Admin - Referans kodunu sil
 */
export async function deleteReferralCode(userId: string): Promise<any> {
  const response = await fetch(`${getBaseUrl()}/api/admin/affiliate/codes/${userId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Kod silinemedi');
  }

  return response.json();
}

/**
 * Admin - Bir referans kodunun getirdiği kullanıcılar
 */
export async function getAffiliateReferredUsers(userId: string): Promise<any[]> {
  const response = await fetch(`${getBaseUrl()}/api/admin/affiliate/codes/${userId}/referred-users`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Getirilen kullanıcılar yüklenemedi');
  }

  return response.json();
}

// ============================================================================
// HELPERS
// ============================================================================

const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:5248' 
  : 'https://amazonbonus-001-site1.mtempurl.com';

function getBaseUrl(): string {
  return API_BASE_URL;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Durum rengini döndürür
 */
export function getStatusColor(status: CommissionPeriod['status']): string {
  switch (status) {
    case 'InProgress':
      return '#3B82F6'; // blue
    case 'AwaitingPayment':
      return '#8B5CF6'; // purple
    case 'PaymentPending':
      return '#F59E0B'; // amber
    case 'Paid':
      return '#10B981'; // green
    case 'CarriedOver':
      return '#0EA5E9'; // sky — devredildi
    default:
      return '#6B7280'; // gray
  }
}

/**
 * Durum açıklamasını Türkçe döndürür
 */
export function getStatusText(status: CommissionPeriod['status']): string {
  switch (status) {
    case 'InProgress':
      return 'Devam Ediyor';
    case 'AwaitingPayment':
      return 'Ödeme Tarihi Bekleniyor';
    case 'PaymentPending':
      return 'Ödeme Bekleniyor';
    case 'Paid':
      return 'Ödendi';
    case 'CarriedOver':
      return 'Sonraki Döneme Devredildi';
    default:
      return 'Bilinmiyor';
  }
}

/**
 * Para formatı
 */
export function formatCurrency(amount: number): string {
  // Gizlilik modu tek merkezden (utils/format) yönetilir — bkz. earnings-api.
  if (isPanelMasked()) return PRIVACY_MASK;
  return `$${amount.toFixed(2)}`;
}

/**
 * Tarih formatı (Türkçe)
 */
export function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Kısa tarih formatı
 */
export function formatShortDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('tr-TR');
}